import { TwiageCase } from "@/Case/models/TwiageCase";
import { FeatureFlags } from "@/CaseFeedback/store/FeatureFlags";
import { hasSupportedFeature } from "@/CaseFeedback/store/featureResolver";
import AuthorizationService from "@/Login/services/Authorization.Service";
import { TeamSelectionService } from "@/TeamSelection/services/TeamSelection.Service";
import { computed, reactive, toRefs } from "vue";
import { getProtocolStoreInitialState } from "../functions/getProtocolStoreInitialState";
import * as protocolService from "../protocol.service";
import { Exchange, FeedbackType } from "../types/Exchange";
import { FeedbackEvent } from "../types/FeedbackEvent";
import { EmtLevel } from "../types/QuestionParams";
import { prepareQuestionParams } from "./prepareQuestionParams";

export const state = reactive(getProtocolStoreInitialState());

/**
 * Resets the conversation state by clearing the conversation array
 * and clearing the session ID so the server generates a new one
 */
const resetConversation = (): void => {
  state.conversation = [];
  state.chatSessionID = undefined;
};

export const useProtocolStore = () => {
  const selectedTeam = TeamSelectionService.getStoredTeam();

  const handleNewQuestion = async (question?: string) => {
    state.conversation.push({
      summarizedResult: "",
      detailedResult: "",
      questionDatetime: new Date(),
      question,
      pageLinks: [],
      streaming: true,
      detailedLoading: false,
    });

    try {
      state.askingQuestion = true;
      const currentExchangeIndex = state.conversation.length - 1;
      const showAnswer = shouldShowAnswer.value;

      await protocolService.askQuestion({
        data: prepareQuestionParams(question),
        onStream: (textResponse, isComplete, finalData) => {
          if (showAnswer) {
            updateExchangeAtIndex(currentExchangeIndex, {
              summarizedResult: textResponse,
              streaming: !isComplete,
            });
          } else if (finalData?.pageLinks?.length) {
            updateExchangeAtIndex(currentExchangeIndex, {
              streaming: false,
              pageLinks: finalData.pageLinks,
              answerDatetime: new Date(),
              questionUuid: finalData.questionUuid,
            });
          }

          if (finalData && isComplete) {
            updateExchangeAtIndex(currentExchangeIndex, {
              answerDatetime: new Date(),
              pageLinks: finalData.pageLinks || [],
              questionUuid: finalData.questionUuid,
              summarizedResult: showAnswer ? textResponse || finalData.summarizedResult : "",
              detailedResult: showAnswer ? finalData.detailedResult : "",
              streaming: false,
            });

            if (finalData.chatSessionID) {
              state.chatSessionID = finalData.chatSessionID;
            }
          }
        },
        skipStreaming: !showAnswer,
      });
    } catch (error) {
      console.error("Error processing protocol request:", error);
      handleApiError(state.conversation.length - 1, "Sorry, there was an error processing your request.");
    } finally {
      state.askingQuestion = false;
    }
  };

  const handleNewContext = () => {
    resetConversation();
  };

  const handleApiError = (index: number, errorMessage: string) => {
    updateExchangeAtIndex(index, {
      summarizedResult: errorMessage,
      detailedResult: errorMessage,
      answerDatetime: new Date(),
      pageLinks: [],
      streaming: false,
      detailedLoading: false,
    });
  };

  const updateExchangeAtIndex = (index: number, updates: Partial<Exchange>) => {
    if (index === -1 || !state.conversation[index]) return;

    Object.assign(state.conversation[index], updates);
  };

  const updateExchangeByUuid = (uuid: string, updates: Partial<Exchange>) => {
    const index = state.conversation.findIndex((e) => e.questionUuid === uuid);
    if (index !== -1) {
      updateExchangeAtIndex(index, updates);
    }
    return index;
  };

  const getDetailedResponse = async (questionUuid: string, question: string) => {
    try {
      const exchangeIndex = updateExchangeByUuid(questionUuid, {
        detailedLoading: true,
        detailedResult: "",
      });

      if (exchangeIndex === -1) return;

      await protocolService.askQuestion({
        data: prepareQuestionParams(question, "detailed", questionUuid),
        onStream: (textResponse, isComplete, finalData) => {
          updateExchangeAtIndex(exchangeIndex, {
            detailedResult: textResponse,
            detailedLoading: !isComplete,
          });

          if (isComplete && finalData && finalData.detailedResult) {
            updateExchangeAtIndex(exchangeIndex, {
              detailedResult: finalData.detailedResult,
              detailedLoading: false,
            });
          } else if (isComplete) {
            updateExchangeAtIndex(exchangeIndex, { detailedLoading: false });
          }
        },
      });
    } catch (error) {
      console.error("Error getting detailed response:", error);
      const exchangeIndex = state.conversation.findIndex((e) => e.questionUuid === questionUuid);
      if (exchangeIndex !== -1) {
        handleApiError(exchangeIndex, "Sorry, there was an error retrieving the detailed response.");
      }
    }
  };

  const setContext = (twiageCase: TwiageCase | null) => {
    const newCaseId = twiageCase?.id ?? undefined;
    if (newCaseId !== state.currentCaseId) {
      state.currentCaseId = newCaseId;
      resetConversation();
    }
  };

  const handleFeedback = (event: FeedbackEvent) => {
    const { questionUuid, feedbackType, feedbackText } = event;
    updateExchangeByUuid(questionUuid, {
      feedbackType,
      feedbackText: feedbackText || undefined,
    });
  };

  const submitFeedback = async (questionUuid: string, isPositive: boolean, feedbackText?: string) => {
    state.sendingFeedback = true;
    try {
      const data = {
        questionUuid,
        feedback: isPositive,
        feedbackText,
      };

      await protocolService.questionFeedback({
        data,
      });

      handleFeedback({
        questionUuid,
        feedbackType: isPositive ? FeedbackType.Positive : FeedbackType.Negative,
        feedbackText,
      });
    } catch (error) {
      console.warn("Error submitting protocol feedback:", error);
    } finally {
      setTimeout(() => {
        state.sendingFeedback = false;
      }, 1000);
    }
  };

  const shouldShowAnswer = computed(() => {
    return !hasSupportedFeature({
      organization: selectedTeam,
      feature: FeatureFlags.aiProtocolHideSummary,
    });
  });

  const shouldShowFeedback = computed(() => {
    return hasSupportedFeature({
      organization: selectedTeam,
      feature: FeatureFlags.showAiProtocolFeedback,
    });
  });

  const getUniqueLinks = (pageLinks?: string[]) => {
    return Array.from(new Set(pageLinks));
  };

  const saveEmsLevel = (emsLevel: EmtLevel) => {
    state.shouldShowEmtLevel = false;
    protocolService.setEmtLevel(AuthorizationService.getUser(), emsLevel);
  };

  const emsLevels = computed(() => {
    const emsAgencyEmtLevels = selectedTeam.codeVital?.emtLevels ?? [];

    const formattedAgencies = emsAgencyEmtLevels.map((level) => ({
      id: level.name,
      label: level.name,
    }));

    return formattedAgencies;
  });

  return {
    ...toRefs(state),
    handleNewQuestion,
    handleFeedback,
    handleNewContext,
    submitFeedback,
    getDetailedResponse,
    setContext,
    getUniqueLinks,
    shouldShowAnswer,
    shouldShowFeedback,
    emsLevels,
    saveEmsLevel,
  };
};
